import React, {useContext, useEffect} from "react";
import {useLocation} from "react-router-dom";

import mistEliminators from "../../../assets/images/solutions/MeshPads.png";
import CompanyCarousel from "../../../components/carousel";
import Footer from "../../../components/footer";
import Header from "../../../components/header";
import * as S from "./styles";
import GetInTouch from "../../../components/getInTouch";
import Questions from "../../../components/questions";
import Breadcrumbs from "../../../components/breadcrumbs";
import {GlobalContext} from "../../../context/global/globalContext";

function MeshPads() {
  const {setBreadcrumbs} = useContext(GlobalContext);
  const location = useLocation();

  useEffect(() => {
    setBreadcrumbs([
      {label: "Home", path: "/"},
      {label: "Technologies", path: "/technologies"},
      {label: "Mesh Pads", path: "/technologies/meshpads"},
    ]);
  }, [location, setBreadcrumbs]);

  const questionsData = [
    {
      question: "What are mesh pads used for?",
      answer: "They’re used for droplet removal, particularly in vertical gas flow systems.",
    },
    {
      question: "Can you customize mesh density?",
      answer: "Yes, material and mesh structure are selected based on the application.",
    },
    {
      question: "How do I know if a mesh pad is the right solution?",
      answer: "We can assess your process data and suggest optimal separation equipment.",
    },
  ];

  return (
    <>
      <Header />
      <S.Container>
        <Breadcrumbs />

        <S.MainHeading>Mesh Pads</S.MainHeading>

        <S.TwoColumnSection>
          <S.Column>
            <S.Paragraph>
              Mesh pads—also known as demisters or droplet separators—are essential components used
              to remove fine liquid droplets from gas streams. Installed in gas scrubbers,
              distillation columns, or stripping towers, these units significantly improve
              separation efficiency and protect downstream equipment.
            </S.Paragraph>
            <S.Paragraph>
              With our wide range of material options and custom-engineered configurations,
              Ecosystems Europe delivers high-performance mesh pad solutions for challenging
              industrial conditions, including high temperatures and corrosive environments.
            </S.Paragraph>
          </S.Column>
          <S.ImageContainer>
            <img src={mistEliminators} alt="Mist Eliminators Equipment" />
          </S.ImageContainer>
        </S.TwoColumnSection>

        <S.Section>
          <S.SectionTitle>How Mesh Pads Work</S.SectionTitle>
          <S.Paragraph>
            Mesh pads operate by capturing droplets as they collide and impact the mesh structure.
            As the gas passes through the mesh, liquid droplets are forced to contact the surface of
            the fibers, where they coalesce and drain away under gravity.
          </S.Paragraph>
          <S.Paragraph>
            Depending on the process, mesh pads can be produced from a variety of materials such as:
          </S.Paragraph>

          <S.List>
            <S.BulletItem color="black">
              Metals: Stainless steel 304, 316, Hastelloy, Alloy 20, Monel
            </S.BulletItem>
            <S.BulletItem color="black">Plastics: PP, PE, PTFE (PVDF), Hostaflon</S.BulletItem>
            <S.BulletItem color="black">
              Fiberglass and PTFE fibers: Used for co-knitting to improve fine mist capture
            </S.BulletItem>
          </S.List>

          <S.Paragraph>
            This design allows mesh pads to efficiently separate droplets as small as 0,5 microns,
            with extremely low pressure drop and minimal maintenance.
          </S.Paragraph>
        </S.Section>

        <S.TwoColumnSection>
          <S.Column>
            <S.SectionTitle>Process Design</S.SectionTitle>
            <S.List>
              <S.BulletItem color="blue">Temperature Range: -10 to 200 °C </S.BulletItem>
              <S.BulletItem color="blue">
                Flow Rates: Designed for 500 to 100,000 Nm³/h
              </S.BulletItem>
              <S.BulletItem color="blue">Operating Pressure: -50 to +75 mbar</S.BulletItem>
              <S.BulletItem color="blue">Pressure Drop: 10 – 40 mbar</S.BulletItem>
              <S.BulletItem color="blue">Efficiency: up to 99,9% removal efficiency</S.BulletItem>
            </S.List>
          </S.Column>
          <S.Column>
            <S.SectionTitle>Materials</S.SectionTitle>
            <S.List>
              <S.BulletItem color="blue">Glass fiber - Co-knit</S.BulletItem>
              <S.BulletItem color="blue">
                Stainless steel (316L, 304L, 904L, high-nickel alloys)
              </S.BulletItem>
              <S.BulletItem color="blue">Monel, Duplex and MECS®ZeCor®</S.BulletItem>
              <S.BulletItem color="blue">Hostaflon, PVDF, PTFE, PE, and PP</S.BulletItem>
            </S.List>
          </S.Column>
        </S.TwoColumnSection>

        <S.QuestionsSpacing>
          <Questions items={questionsData} />
        </S.QuestionsSpacing>

        <S.ContainerTitles>
          <S.MiniTitle>Our Partners</S.MiniTitle>
          <S.Title>
            We partner with industry leaders to create clean alternatives to traditional industrial
            processes
          </S.Title>
        </S.ContainerTitles>

        <CompanyCarousel />
        <GetInTouch />
      </S.Container>
      <Footer />
    </>
  );
}

export default MeshPads;
